import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CaretRight, CaretLeft } from '~/ui/assets/icons';
import { useTheme } from 'styled-components';
import { Typography } from '~/ui/components';
import { PaginationContent, Item, Wrapper, Button, SelectContainer, SelectLabel, Select } from './Pagination.styled';

interface IPaginationProps {
  totalPages: number;
  onClick: (page: number) => void;
  resetCurrentPage?: boolean;
  initialPage?: number;
  pageSize?: number;
  totalItems?: number;
  onPageSizeChange?: (newSize: number) => void;
  pageSizeOptions?: number[];
  loading?: boolean | 1 | 0;
}

const MAX_ITEMS = 9;
const MAX_LEFT = (MAX_ITEMS - 1) / 2;

export function Pagination({
  totalPages,
  onClick,
  resetCurrentPage = false,
  initialPage = 1,
  pageSize = 50,
  totalItems = 0,
  pageSizeOptions = [10, 20, 50, 100, 150],
  onPageSizeChange,
  loading,
}: IPaginationProps) {
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const theme = useTheme();

  const maxFirstPage: number = useMemo(() => Math.max(totalPages - (MAX_ITEMS - 1), 1), [totalPages, MAX_ITEMS]);

  const firstPage: number = useMemo(
    () => Math.min(Math.max(currentPage - MAX_LEFT, 1), maxFirstPage),
    [currentPage, maxFirstPage],
  );

  const handleOnClick = useCallback(
    (page) => {
      setCurrentPage(page);
      onClick(page);
    },
    [onClick],
  );

  useEffect(() => {
    if (resetCurrentPage) {
      setCurrentPage(1);
    }
  }, [resetCurrentPage]);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  const startItem = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <Wrapper loading={loading}>
      <span>
        {startItem} - {endItem} de {totalItems} itens
      </span>

      <PaginationContent>
        <Item disabled={currentPage === 1}>
          <Button disabled={currentPage === 1} type="button" onClick={() => handleOnClick(currentPage - 1)}>
            <CaretLeft
              color={currentPage === 1 ? theme.colors.element.disabled : theme.colors.brand.primary}
              data-testid="previous-page-icon"
              height={24}
              width={24}
            />
          </Button>
        </Item>

        {Array.from({ length: Math.min(MAX_ITEMS, totalPages) }).map((_, index) => (
          <Item currentPage={currentPage === index + firstPage} key={index}>
            <Button type="button" onClick={() => handleOnClick(index + firstPage)}>
              <Typography type="bodySmall">{index + firstPage}</Typography>
            </Button>
          </Item>
        ))}

        <Item disabled={currentPage === totalPages}>
          <Button disabled={currentPage === totalPages} type="button" onClick={() => handleOnClick(currentPage + 1)}>
            <CaretRight
              height={12}
              width={12}
              color={currentPage === totalPages ? theme.colors.element.disabled : theme.colors.brand.primary}
              data-testid="next-page-icon"
            />
          </Button>
        </Item>
      </PaginationContent>

      <SelectContainer>
        <SelectLabel>Itens por página:</SelectLabel>
        <Select
          value={pageSize}
          onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
          data-testid="items-per-page-select"
          name="page-size"
        >
          {pageSizeOptions?.map((option) => (
            <option value={option}>{option}</option>
          ))}
        </Select>
      </SelectContainer>
    </Wrapper>
  );
}
